/**
 * Pushes analytics data for the "Clear All" filters action.
 * @return {void}
 */
import { getPageType } from "@/utils/helper"

export const getAnalyticsDataForClearAll = () => {
  const eventInfo = {
    eventAction: "plp clear all filter",
    eventName: "plp clear all filter",
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: "clear all",
    internalLinkPosition: "plp",
    internalLinkType: "plp:clear all",
    internalLinkZoneName: "plp:filters",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  const pageEvent = window.pageObj || {}
  const pageType = getPageType()

  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

/**
 * Pushes analytics data for closing the filter overlay.
 * @return {void}
 */
export const getAnalyticsDataForFilterCloseOverlay = () => {
  const pageEvent = window.pageObj || {}
  const eventInfo = {
    eventAction: "plp filter close",
    eventName: "plp filter close",
    eventType: "informative",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: "close",
    internalLinkPosition: "close",
    internalLinkType: "plp:close",
    internalLinkZoneName: "plp:filters",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  const pageType = getPageType()
  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

/**
 * Pushes analytics data for the "View All Results" CTA.
 * @return {void}
 */
export const getAnalyticsDataForViewAllResultsCTA = () => {
  const pageEvent = window.pageObj || {}
  const eventInfo = {
    eventAction: "plp filter view result",
    eventName: "plp filter view result",
    eventType: "informative",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: "view result",
    internalLinkPosition: "plp",
    internalLinkType: "plp:filters",
    internalLinkZoneName: "plp:filters",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    filterArray: "",
  }
  const pageType = getPageType()
  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

/**
 * Pushes analytics data for social share actions.
 * @param {boolean} clickInternal
 * @param {string} name
 * @param {string} link
 * @param {string} share
 * @return {void}
 */
export const getAnalyticsDataForSocialShare = (
  clickInternal,
  name,
  link,
  share
) => {
  const eventInfo = {
    eventAction: "plp share",
    eventName: "plp share",
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${name}`,
    internalLinkPosition: "plp",
    internalLinkType: "plp:share",
    internalLinkZoneName: "plp:promo tiles",
    internalLinkURL: link,
    clickInternalLinks: `${clickInternal}`,
  }
  if (share) {
    return {
      "data-datalayer-id": "kds-element-plp",
      "data-datalayer-data": JSON.stringify(eventInfo),
    }
  }
  const pageEvent = window.pageObj || {}
  const pageType = getPageType()
  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

/**
 * Pushes analytics data for showing or hiding filter options.
 * @param {boolean} showHide
 * @return {void}
 */
export const getShowHideFilterOptionAnalyticsData = showHide => {
  const state = showHide ? "show" : "hide"
  const pageEvent = window.pageObj || {}
  const eventInfo = {
    clickInternalLinks: "true",
    eventAction: `plp:${state} filter`,
    eventName: `plp:${state} filter`,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${state} filters`,
    internalLinkType: `plp:${state}`,
    internalLinkURL: "n/a",
    internalLinkPosition: "plp",
    internalLinkZoneName: "plp:filters",
  }
  const pageType = getPageType()
  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

/**
 * Pushes analytics data for sort by filter click.
 * @param {string} e
 * @return {void}
 */
export const getAnalyticsDataForSortByClick = e => {
  const pageEvent = window.pageObj || {}
  const eventInfo = {
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${e.toLocaleLowerCase()}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    eventAction: "plp sort by filter",
    eventName: "plp sort by filter",
    internalLinkPosition: "plp",
    internalLinkType: "plp:sort by",
    internalLinkZoneName: "plp:sort by filter",
  }
  const pageType = getPageType()
  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

/**
 * Pushes analytics data for facet checkbox filter.
 * @param {string} actionType - The type of filter action (e.g., "add", "remove").
 * @param {string} value - The value of the facet checkbox.
 * @param {string} facet - The name of the facet.
 * @param {Array} filterArray - The array of currently applied filters.
 * @return {void}
 */
export const getFilterAnalyticsForFacetCheckbox = (
  actionType,
  value,
  facet,
  filterArray
) => {
  const eventData = {
    eventAction: `plp filter ${actionType}`,
    eventName: `plp filter ${actionType}`,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: value,
    internalLinkPosition: "plp",
    internalLinkType: `plp:${facet ? facet.toLowerCase() : ""}`,
    internalLinkZoneName: "plp:filters",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    filterArray: filterArray,
  }

  const pageEvent = window.pageObj || {}
  const pageType = getPageType()

  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }

  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo: eventData,
  })
}

/**
 * Pushes analytics data for print or share actions on the PLP.
 * @param {string} printOrShare - The action type, either "print" or "share".
 * @return {void}
 */
export const addPrintOrShareAnalytics = printOrShare => {
  const eventInfo = {
    eventAction: `plp ${printOrShare}`,
    eventName: `plp ${printOrShare}`,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: printOrShare,
    internalLinkPosition: "plp",
    internalLinkType: `plp:${printOrShare}`,
    internalLinkZoneName: "plp:filters",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  const pageEvent = window.pageObj || {}
  const pageType = getPageType()

  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

export const FILTER = {
  EXPAND: "expand",
  COLLAPSE: "collapse",
  FILTERS: "filters",
  HIDE_DISCONTINUED: "hide discontinued products",
}

/**
 * Pushes analytics data for expanding or collapsing a filter group on the PLP.
 * @param {boolean} collapseState - True if the filter group is expanded, false if collapsed.
 * @param {string} facetName - The name of the facet being expanded or collapsed.
 * @return {void}
 */
export const addCollapseAnalytics = (collapseState, facetName) => {
  const eventInfo = {
    eventAction: `plp filter ${
      collapseState ? FILTER.EXPAND : FILTER.COLLAPSE
    }`,
    eventName: `plp filter ${collapseState ? FILTER.EXPAND : FILTER.COLLAPSE}`,
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: collapseState ? FILTER.EXPAND : FILTER.COLLAPSE,
    internalLinkPosition: "plp",
    internalLinkType: "plp:" + facetName.toLowerCase(),
    internalLinkZoneName: "plp:filters",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  }
  const pageEvent = window.pageObj || {}
  const pageType = getPageType()

  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo,
  })
}

export const getTrackingAttributesForSort = label => {
  return JSON.stringify({
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: `${label.toLowerCase()}`,
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
    eventAction: "plp sort by filter",
    eventName: "plp sort by filter",
    internalLinkPosition: "plp",
    internalLinkType: "plp:sort by",
    internalLinkZoneName: "plp:sort by filter",
  })
}

export const getFilterAnalyticsForTagClearCTA = (filterArray, value, facet) => {
  const eventData = {
    eventAction: "plp filter cancel",
    eventName: "plp filter cancel",
    eventType: "navigation",
    filterArray: filterArray,
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: value,
    internalLinkPosition: "plp",
    internalLinkType: `plp:${facet}`,
    internalLinkZoneName: "plp:filters",
    internalLinkURL: window.location.href,
    clickInternalLinks: "true",
  }
  const pageEvent = window.pageObj || {}
  const pageType = getPageType()

  const updatedPageEvent = {
    ...pageEvent,
    pageInfo: {
      ...(pageEvent.pageInfo || {}),
      contentType: pageType,
    },
  }
  window.adobeDataLayer.push({
    event: "cmp:click",
    page: updatedPageEvent,
    eventInfo: eventData,
  })
}

export const getTrackingAttributeForClearAllCTA = () => {
  return JSON.stringify({
    eventAction: "plp clear all filter",
    eventName: "plp clear all filter",
    eventType: "navigation",
    eventMsg: "n/a",
    eventStatus: "n/a",
    internalLinkName: "clear all",
    internalLinkPosition: "plp",
    internalLinkType: "plp:clear all",
    internalLinkZoneName: "plp:filters",
    internalLinkURL: "n/a",
    clickInternalLinks: "true",
  })
}
